import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTextField from "../../components/CTextField";
import CButton from "../../components/CButton";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { saveTrainingCategoryBudgetConfiguration, fetchTrainingCategoryBudgetConfiguration } from "../../slices/trainingCategoryBudgetSlice";
import { fetchYearId } from "../../slices/yearSlice";
import { setTotalBudget, fetchTotalBudget, saveTotalBudget } from "../../slices/totalBudgetSlice";
import { toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import { messages } from "../../constant/alertMessages";

export default function TotalBudget() {
  const dispatch = useDispatch();
  
  const companyId = JSON.parse(localStorage.getItem("sdfUser"))?.company_id;
  const setaFields = useSelector(state => state.setaTemplate.setaTemplate);
  const { budgetConfiguration, loading } = useSelector(state => state.trainingCategoryBudget);
  const { totalBudget, loading: totalBudgetLoading } = useSelector(state => state.totalBudget);
  const [yearId, setYearId] = useState(null);
  
  const [budgetData, setBudgetData] = useState([]);
  const [trainingTypeOptions, setTrainingTypeOptions] = useState([]);
  const [error, setError] = useState(null)
  const [totalData, setTotalData] = useState({
    percent: "100.0%",
    rand: "0",
    m: "0",
    f: "0",
  });

  useEffect(() => {
    const totalBudgetValue = parseFloat(totalBudget) || 0;
    const totalRandValue = budgetData.reduce(
      (sum, item) => sum + (parseFloat(item.budget) || 0),
      0
    );

    if (totalBudgetValue.toFixed(2) !== totalRandValue.toFixed(2)) {
      setError(messages.MISMATCH_BUDGET(totalBudgetValue, totalRandValue));
    } else {
      setError(null);
    }
  }, [budgetData, totalBudget]);

  // Fetch SETA template when component mounts
  useEffect(() => {
    if (companyId) {
      dispatch(fetchSetaTemplateByCompanyId(companyId));
      dispatch(fetchYearId()).then(res => setYearId(res.payload));
    }
  }, [companyId, dispatch]);

  // Fetch total budget from database when yearId is available
  useEffect(() => {
    if (companyId && yearId) {
      dispatch(fetchTotalBudget({ yearId, companyId }));
    }
  }, [companyId, yearId, dispatch]);

  // Fetch existing budget configuration
  useEffect(() => {
    if (companyId && yearId) {
      dispatch(fetchTrainingCategoryBudgetConfiguration({ yearId, companyId }));
    }
  }, [companyId, yearId, dispatch]);

  // Extract training type options from SETA fields
  useEffect(() => {
    if (setaFields && Array.isArray(setaFields)) {
      const trainingTypeField = setaFields.find(
        field => field.config?.isTrainingTypeField
      );
      if (trainingTypeField && Array.isArray(trainingTypeField.allowedValues)) {
        const options = trainingTypeField.allowedValues.map(val => ({
          id: val,
          value: val,
          label: val,
        }));
        setTrainingTypeOptions(options);
        
        // Initialize budget data with training categories from SETA fields
        const initialBudgetData = options.map(option => ({
          trainingType: option.label,
          percentage: "0.0",
          budget: "0",
          headCount: {
            male: "0",
            female: "0"
          }
        }));
        setBudgetData(initialBudgetData);
      }
    }
  }, [setaFields]);

  // Load existing configuration when available
  useEffect(() => {
    if (budgetConfiguration && budgetConfiguration.length > 0) {
      setBudgetData(budgetConfiguration.map(item => ({
        trainingType: item.trainingType,
        percentage: item.percentage?.toString() || "0.0",
        budget: item.budget?.toString() || "0",
        headCount: {
          male: item.headCount?.male?.toString() || "0",
          female: item.headCount?.female?.toString() || "0"
        }
      })));
    }
  }, [budgetConfiguration]);

  // Calculate totals automatically when budgetData changes
  useEffect(() => {
    if (budgetData.length > 0) {
      const totals = budgetData.reduce((acc, item) => {
        acc.percentage += parseFloat(item.percentage) || 0;
        acc.budget += parseFloat(item.budget) || 0;
        acc.male += parseInt(item.headCount.male) || 0;
        acc.female += parseInt(item.headCount.female) || 0;
        return acc;
      }, { percentage: 0, budget: 0, male: 0, female: 0 });

      setTotalData({
        percent: totals.percentage.toFixed(2),
        rand: totals.budget.toFixed(2),
        m: totals.male.toString(),
        f: totals.female.toString(),
      });
    }
  }, [budgetData]);

  useEffect(() => {
    handleSync()
  }, [totalBudget]);

  const handleFieldChange = (index, field, value) => {
    const updatedData = [...budgetData];
    
    // Validate percentage field to not exceed 100%
    if (field === 'percentage') {
      // Only allow numbers and decimal points
      const regex = /^\d*\.?\d*$/;
      if (!regex.test(value) && value !== "") {
        return;
      }
      
      // Limit to 2 decimal places
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 2) {
        return;
      }
      
      const numValue = parseFloat(value) || 0;
      if (numValue > 100) {
        toast.error("Percentage cannot exceed 100%");
        return;
      }

      // Calculate budget value when percentage changes
      const totalBudgetValue = parseFloat(totalBudget) || 0;
      const calculatedBudget = (numValue / 100) * totalBudgetValue;
      updatedData[index].budget = calculatedBudget.toFixed(2);
    }

    // Validate numeric input for budget field
    if (field === 'budget') {
      // Only allow numbers and decimal points
      const regex = /^\d*\.?\d*$/;
      if (!regex.test(value) && value !== "") {
        return;
      }
      
      // Limit to 2 decimal places
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 2) {
        return;
      }

      // Calculate percentage value when budget changes
      const totalBudgetValue = parseFloat(totalBudget) || 0;
      const budgetValue = parseFloat(value) || 0;
      if (totalBudgetValue > 0) {
        const calculatedPercentage = (budgetValue / totalBudgetValue) * 100;
        updatedData[index].percentage = calculatedPercentage.toFixed(2);
      }
    }

    // Validate integer input for M and F fields
    if (field === 'male' || field === 'female') {
      // Only allow numbers (no decimal points)
      const regex = /^\d*$/;
      if (!regex.test(value) && value !== "") {
        return;
      }
      
      // Limit to reasonable head count (e.g., 10000)
      const numValue = parseInt(value) || 0;
      if (numValue > 10000) {
        return;
      }
    }
    
    if (field === 'male' || field === 'female') {
      updatedData[index].headCount[field] = value;
    } else {
      updatedData[index][field] = value;
    }
    setBudgetData(updatedData);
  };

  const handleTotalBudgetChange = (value) => {
    // Only allow numbers and decimal points
    const regex = /^\d*\.?\d*$/;
    if (!regex.test(value) && value !== "") {
      return;
    }
    
    // Limit to 2 decimal places
    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) {
      return;
    }

    // Update local state only (no database save)
    dispatch(setTotalBudget(value));
    
    // Recalculate all budget values based on new total budget
    const totalBudgetValue = parseFloat(value) || 0;
    const updatedData = budgetData.map(item => {
      const percentage = parseFloat(item.percentage) || 0;
      const calculatedBudget = (percentage / 100) * totalBudgetValue;
      return {
        ...item,
        budget: calculatedBudget.toFixed(2)
      };
    });
    setBudgetData(updatedData);
  };

  const handleSync = async () => {
    const totalBudgetValue = parseFloat(totalBudget) || 0;
    setBudgetData(prevData =>
      prevData.map(item => {
        const percentage = parseFloat(item.percentage) || 0;
        const calculatedBudget = (percentage / 100) * totalBudgetValue;
        return {
          ...item,
          budget: calculatedBudget.toFixed(2)
        };
      })
    );
    setError(null)

  }
  const handleSave = async () => {
    try {
      if (!companyId || !yearId) {
        toast.error("Company ID or Year ID not available");
        return;
      }

      // Validate total percentage equals 100%
      const totalPercentage = budgetData.reduce((sum, item) => sum + (parseFloat(item.percentage) || 0), 0);
      if (Math.abs(totalPercentage - 100) > 0.1) { // Allow small rounding differences
        toast.error(`Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(2)}%`);
        return;
      }

      // Validate total budget equals total rand (when percentage = 100%)
      const totalBudgetValue = parseFloat(totalBudget) || 0;
      const totalRandValue = budgetData.reduce((sum, item) => sum + (parseFloat(item.budget) || 0), 0);
      if (totalBudgetValue.toFixed(2) !== totalRandValue.toFixed(2)) { // Allow small rounding differences
        toast.error(messages.MISMATCH_BUDGET(totalBudgetValue.toFixed(2), totalRandValue.toFixed(2)));
        return;
      }

      // Transform data to match the required format
      const transformedData = budgetData.map(item => ({
        trainingType: item.trainingType,
        percentage: parseFloat(item.percentage) || 0,
        budget: parseFloat(item.budget) || 0,
        headCount: {
          male: parseInt(item.headCount.male) || 0,
          female: parseInt(item.headCount.female) || 0
        }
      }));

      // Save training category budget configuration
      await dispatch(saveTrainingCategoryBudgetConfiguration({
        budgetData: transformedData,
        total_budget: totalBudget,
        yearId,
        companyId
      }))


      toast.success("Training category budget configuration saved successfully!");
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error("Failed to save training category budget configuration");
    }
  };

  return (
    <div>
      <div className="relative py-[1px]">
        {error && <p className="text-red-500 text-sm leading-sm absolute top-[-12px] w-full text-center">{error}{" "} 
          <Tooltip title="This will automatically distribute the total budget across training types based on their assigned percentages." arrow>
            <span onClick={handleSync} className=" underline underline-offset-1 cursor-pointer  ">
            Sync</span> 
          </Tooltip>
        </p>}
      </div>

    <div className="bg-white rounded-lg p-6 border border-blue-200 relative mt-4 flex flex-col gap-4">
      <div className="w-full">
        {/* <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
          <h2 className="text-center text-base font-semibold p-2 bg-white">
            Total Budget Configuration
          </h2>
          <div className="p-4">
            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Budget (R)
                </label>
                <CTextField
                  value={totalBudget}
                  onChange={(e) => handleTotalBudgetChange(e.target.value)}
                  className="w-full"
                  placeholder="Enter total budget amount"
                  variant="standard"
                  size="small"
                  disabled={totalBudgetLoading}
                  InputProps={{
                    style: { fontSize: "14px" },
                  }}
                />
              </div>
            </div>
          </div>
        </div> */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <h2 className="text-center text-base font-semibold p-2 bg-white">
            Total Training Budget
          </h2>
          <table className="w-full border-collapse configuration-table">
            <thead>
              <tr className="bg-blue-50 font-semibold border-b border-t border-gray-200">
                <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200 w-4">
                  Category
                </th>
                <th className="font-semibold px-4 py-2 text-center bg-gray-150 border-l-0 text-sm leadimg-sm">
                  %
                </th>
                <th className="font-semibold px-4 py-2   bg-white "></th>
                <th className=" font-semibold px-4 py-2 text-center bg-gray-150 border-l-0 text-sm leadimg-sm">
                  Rand
                </th>
                <th className="font-semibold px-4 py-2 bg-white w-3"></th>
                <th
                  className="font-semibold  px-4 py-2 text-center bg-gray-150 border-r-blue-50 text-sm leadimg-sm"
                  colSpan={3}
                >
                  Head Count
                </th>
              </tr>
              <tr className="bg-blue-50 font-semibold border-b border-gray-200">
                <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                <th className="font-semibold  px-4 py-2 text-center text-sm leadimg-sm"></th>
                <th className="font-semibold w-3 px-4 py-2 text-center text-sm leadimg-sm bg-white"></th>

                <th className="font-semibold  px-4 py-2 text-center text-sm leadimg-sm">
                  Rand
                </th>
                <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                <th className="font-semibold  px-4 py-2 text-center text-sm leadimg-sm">
                  M
                </th>
                <th className="font-semibold  px-4 py-2 text-center text-sm leadimg-sm">
                  F
                </th>
                <th className="font-semibold  px-4 py-2 text-center text-sm leadimg-sm">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {budgetData.map((row, idx) => (
                <tr
                  key={row.trainingType}
                  className="text-center border-b border-gray-200"
                >
                  <td className=" px-4 py-2 text-left bg-white border-l-0 text-sm leadimg-sm font-medium">
                    {row.trainingType}
                  </td>
                  <td className=" px-4 bg-gray-50 py-2 text-sm leadimg-sm">
                    <CTextField
                      value={row.percentage}
                      onChange={(e) =>
                        handleFieldChange(idx, "percentage", e.target.value)
                      }
                      variant="standard"
                      size="small"
                      className="!mt-0"
                      InputProps={{
                        style: { fontSize: "14px", textAlign: "center" },
                      }}
                    />
                  </td>
                  <td className=" px-4  py-2 text-sm w-3 leadimg-sm bg-white"></td>
                  <td className=" px-4 bg-gray-50 py-2 text-sm leadimg-sm">
                    <CTextField
                      value={row.budget}
                      onChange={(e) =>
                        handleFieldChange(idx, "budget", e.target.value)
                      }
                      variant="standard"
                      size="small"
                      className="!mt-0"
                      InputProps={{
                        style: { fontSize: "14px", textAlign: "center" },
                      }}
                    />
                  </td>
                  <td className=" px-4 bg-white py-2 text-sm leadimg-sm"></td>
                  <td className=" px-4 bg-gray-50 py-2 text-sm leadimg-sm">
                    <CTextField
                      value={row.headCount.male}
                      onChange={(e) =>
                        handleFieldChange(idx, "male", e.target.value)
                      }
                      variant="standard"
                      size="small"
                      className="!mt-0"
                      InputProps={{
                        style: { fontSize: "14px", textAlign: "center" },
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 bg-gray-50 border-t border-b border-gray-200">
                    <CTextField
                      value={row.headCount.female}
                      onChange={(e) =>
                        handleFieldChange(idx, "female", e.target.value)
                      }
                      variant="standard"
                      size="small"
                      className="!mt-0"
                      InputProps={{
                        style: { fontSize: "14px", textAlign: "center" },
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 bg-gray-50 text-sm leadimg-sm">
                    <span className="block w-full text-center">
                      {(parseInt(row.headCount.male) || 0) + (parseInt(row.headCount.female) || 0)}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="font-semibold bg-blue-50">
                <td className=" px-4 py-2  text-sm leadimg-sm border-l-blue-100">
                  Total
                </td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center">
                  <div className="text-center font-bold" style={{ fontSize: "14px" }}>
                    {totalData.percent}
                  </div>
                </td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center bg-white"></td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center">
                  <div className="text-center font-bold" style={{ fontSize: "14px" }}>
                    {totalData.rand}
                  </div>
                </td>
                <td className="px-4 py-2 bg-white  border-gray-200"></td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center">
                  <div className="text-center font-bold" style={{ fontSize: "14px" }}>
                    {totalData.m}
                  </div>
                </td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center">
                  <div className="text-center font-bold" style={{ fontSize: "14px" }}>
                    {totalData.f}
                  </div>
                </td>
                <td className=" px-4 py-2 text-sm leadimg-sm text-center">
                  <div className="text-center font-bold" style={{ fontSize: "14px" }}>
                    {(parseInt(totalData.m) || 0) + (parseInt(totalData.f) || 0)}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <CButton
            onClick={handleSave}
            className="bg-blue-500 hover:bg-blue-700 text-white px-6 py-2 rounded-lg border border-blue-500"
            disabled={loading || totalBudgetLoading}
          >
            Save Configuration
          </CButton>
        </div>
      </div>
    </div>
    </div>
  );
}
